/**
 * Įžanginiai likučiai from a file: a trial balance (apyvartų žiniaraštis) exported from the previous program
 * (e.g. Rivilė) as a table. The columns are guessed and the person can change them: the account, and either
 * the closing debit and credit columns or one signed balance column (debit positive).
 */
const ACCOUNT = /^[1-6]\d{0,7}$/;
/** "1 234,56", "1.234,56", "1,234.56", "-12,00", "(12,00)", "12,00 €" → cents; empty or not a number → null. */
export function parseAmount(text) {
    let t = text.replace(/[\s €]/g, '');
    if (!t)
        return null;
    let negative = false;
    if (/^\(.*\)$/.test(t)) {
        negative = true;
        t = t.slice(1, -1);
    }
    if (t.startsWith('-')) {
        negative = !negative;
        t = t.slice(1);
    }
    if (!/^[\d.,]+$/.test(t))
        return null;
    const comma = t.lastIndexOf(','), dot = t.lastIndexOf('.');
    let decimal = comma > dot ? ',' : dot > comma ? '.' : null;
    // One kind of separator, repeated or with three digits after it: thousands ("1.234", "1,234,567"), not cents.
    if (decimal && (comma < 0 || dot < 0) && (t.split(decimal).length > 2 || /^\d{3}$/.test(t.slice(t.lastIndexOf(decimal) + 1)))) {
        t = t.replace(/[.,]/g, '');
        decimal = null;
    }
    if (decimal) {
        const [int, frac = ''] = [t.slice(0, t.lastIndexOf(decimal)), t.slice(t.lastIndexOf(decimal) + 1)];
        t = `${int.replace(/[.,]/g, '')}.${frac}`;
    }
    const value = Math.round(Number(t) * 100);
    return Number.isFinite(value) ? (negative ? -value : value) : null;
}
const fold = (v) => v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
/** Where the header row and the columns probably are. */
export function guessColumns(rows) {
    const width = Math.max(0, ...rows.map((r) => r.length));
    // The account column: the one whose cells most often look like account codes.
    const score = (c) => rows.filter((r) => ACCOUNT.test((r[c] ?? '').trim())).length;
    let account = 0;
    for (let c = 1; c < width; c++)
        if (score(c) > score(account))
            account = c;
    const firstData = rows.findIndex((r) => ACCOUNT.test((r[account] ?? '').trim()));
    const header = Math.max(0, firstData - 1);
    // Header texts of up to two rows above the data (a two-level header: "Likutis pabaigoje" over "D" "K").
    const head = (c) => fold([rows[header - 1]?.[c] ?? '', rows[header]?.[c] ?? ''].join(' '));
    const spread = (c, row) => { for (let k = c; k >= 0; k--)
        if ((row?.[k] ?? '').trim())
            return row[k]; return ''; };
    const full = (c) => fold(`${spread(c, rows[header - 1])} ${rows[header]?.[c] ?? ''}`);
    const numeric = (c) => rows.slice(firstData).filter((r) => parseAmount(r[c] ?? '') !== null).length > 0;
    const cols = [...Array(width).keys()].filter((c) => c !== account);
    const isDebit = (t) => /\bd\b|debet/.test(t);
    const isCredit = (t) => /\bk\b|kredit|credit/.test(t);
    const closing = cols.filter((c) => /pabaig|galutin|closing|pabaigoj/.test(full(c)));
    let debit = closing.find((c) => isDebit(full(c))) ?? null;
    let credit = closing.find((c) => isCredit(full(c))) ?? null;
    let balance = null;
    if (debit === null || credit === null) {
        const numbers = cols.filter(numeric);
        const single = numbers.find((c) => /likut|balans|saldo/.test(head(c)) && !isDebit(head(c)) && !isCredit(head(c)));
        if (single !== undefined && numbers.filter((c) => /likut|balans|saldo/.test(head(c))).length === 1) {
            debit = null;
            credit = null;
            balance = single;
        }
        else if (numbers.length >= 2) {
            debit = numbers.at(-2);
            credit = numbers.at(-1);
        }
        else if (numbers.length === 1) {
            debit = null;
            credit = null;
            balance = numbers[0];
        }
    }
    const name = cols.find((c) => !numeric(c) && rows.slice(firstData).some((r) => (r[c] ?? '').trim().length > 2)) ?? null;
    return { header, account, name, debit, credit, balance };
}
/**
 * The accounts with a balance (debit positive), from the rows under the header. A row whose account has
 * other accounts of the file under it (a group's subtotal, e.g. 27 or 271 above 2711) is left out.
 */
export function openingLines(rows, c) {
    const by = new Map();
    const seen = new Set();
    for (const r of rows.slice(c.header + 1)) {
        const account = (r[c.account] ?? '').trim();
        if (!ACCOUNT.test(account))
            continue;
        seen.add(account);
        const amount = c.balance !== null ? parseAmount(r[c.balance] ?? '') ?? 0
            : (parseAmount(r[c.debit ?? -1] ?? '') ?? 0) - (parseAmount(r[c.credit ?? -1] ?? '') ?? 0);
        if (!amount)
            continue;
        const line = by.get(account) ?? { account, name: c.name !== null ? (r[c.name] ?? '').trim() : '', balance: 0 };
        line.balance += amount;
        by.set(account, line);
    }
    return [...by.values()].filter((l) => l.balance && ![...seen].some((a) => a !== l.account && a.startsWith(l.account)));
}
//# sourceMappingURL=opening.js.map